import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("en-AU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch (_e) {
    return iso;
  }
};

const Admin = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/logs`, { password });
      if (data.success) {
        setLogs(data.logs);
      } else {
        setError(data.message || "Incorrect password");
      }
    } catch (_err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink)] hover:text-[var(--ink-dark)] mb-5">
          <ArrowLeft size={15} /> Back to resume
        </Link>

        {!logs ? (
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-[0_20px_60px_-15px_rgba(30,32,80,0.25)] ring-1 ring-black/5 p-7">
            <div className="flex items-center gap-2 mb-1 text-[var(--ink)]">
              <ShieldCheck size={18} />
              <h2 className="text-[15px] font-semibold uppercase tracking-wider">Admin Access</h2>
            </div>
            <p className="text-[13px] text-[var(--muted)] mb-5">Enter the admin password to view the access log.</p>
            <form onSubmit={submit} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="pw" className="text-[13px]">Password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              {error && <p className="text-[13px] text-destructive font-medium">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-[var(--ink)] hover:bg-[var(--ink-dark)] text-white">
                {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
                {loading ? "Checking…" : "View Access Log"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_20px_60px_-15px_rgba(30,32,80,0.25)] ring-1 ring-black/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[16px] font-semibold text-[var(--ink-dark)] uppercase tracking-wider">Access Log</h2>
              <span className="text-[13px] text-[var(--muted)]">{logs.length} total</span>
            </div>
            {logs.length === 0 ? (
              <p className="text-[14px] text-[var(--muted)] py-6 text-center">No one has accessed the resume yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Date & time granted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="font-medium">{l.firstName} {l.lastName}</TableCell>
                        <TableCell>{l.email}</TableCell>
                        <TableCell>{l.mobile}</TableCell>
                        <TableCell className="font-mono text-[12px]">{l.code}</TableCell>
                        <TableCell>{formatDate(l.grantedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
